import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { PRODUCT_VALUES_FOR_3B, STATUS_OK, THREEBTYPES } from 'constants/constants';
import { AdvisoryInvestmentsResponse, AdvisoryPortfolioDataResponse } from 'core/types/api/Api';
import { useCreatePortfolioActions, useCreatePortfolioSelectors, useCreatePortfolioState } from 'datasource/CreatePortfolio';
import { TPageError } from 'datasource/PortfolioProcessLayout/PortfolioFlowLayoutManager.types';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { useClientProfile } from 'prodivers/clientProfile';
import { useQueryClient } from '@tanstack/react-query';

export type InvestmentDepot = {
    portfolioId: number;
    value: number;
    shares?: number;
    modelPortfolioName: string;
    name: string;
}

export const useInvestDepots = () => {
    const queryClient = useQueryClient();
    const {
        advisoryIdSelector, isJointAccountSelector, advisoryDataSelector,
    } = useCreatePortfolioSelectors();

    const { portfolioId } = useCreatePortfolioState();
    const { saveAdvisoryDataAction } = useCreatePortfolioActions();
    const { sectionedPortfolios, isOverviewLoading } = useClientProfile();

    const previousExcludedInvestmentRecord = useMemo(() => advisoryDataSelector?.metadata?.investments?.excludedPortfolios
        ?.filter(p => p?.portfolioId !== portfolioId)
        ?.map(portfolio => portfolio?.portfolioId), [advisoryDataSelector?.metadata?.investments?.excludedPortfolios, portfolioId]);

    const portfolios: AdvisoryPortfolioDataResponse[] = useMemo(() => sectionedPortfolios?.jointRegularPortfolios?.filter(
        portfolio => portfolio?.Status?.value === 'active'
        && [...PRODUCT_VALUES_FOR_3B]?.includes(portfolio?.ProductId)
        && (isJointAccountSelector ? portfolio?.AccountHolder?.length > 1 : portfolio?.AccountHolder?.length < 2),
    )?.map(p => ({
        portfolioId: p?.Id,
        name: p?.Name?.label,
        modelPortfolioName: `${p?.ProductExternalId !== THREEBTYPES.pk ? `${p?.Product} -` : ''} ${p?.Profile}`,
        value: p?.rawValue || 0,
        checked: !previousExcludedInvestmentRecord?.includes(p?.Id) || p?.Id === portfolioId,
        disabled: p?.Id === portfolioId,
    })), [sectionedPortfolios, isJointAccountSelector, previousExcludedInvestmentRecord, portfolioId]);

    const [touched, setIsTouched] = useState<boolean>(false);
    const [continueButtonLoading, setContinueButtonLoading] = useState<boolean>(false);
    const [pageErrors, setPageErrors] = useState<TPageError | undefined>(undefined);

    const initialInvestments = useMemo(() => ({
        includedPortfolios: portfolios?.filter(port => !previousExcludedInvestmentRecord?.includes(port?.portfolioId))?.map(({ portfolioId, value }) => ({ portfolioId, value })),
        excludedPortfolios: portfolios?.filter(port => previousExcludedInvestmentRecord?.includes(port?.portfolioId))?.map(({ portfolioId, value }) => ({ portfolioId, value })) || [],
    }), [portfolios, previousExcludedInvestmentRecord]);

    const [investments, setInvestments] = useState<AdvisoryInvestmentsResponse>(initialInvestments);

    useEffect(() => {
        setInvestments(initialInvestments);
    }, [initialInvestments]);

    const handleProfileSelection = useCallback(({ checked, item }) => {
        setInvestments((prevState: AdvisoryInvestmentsResponse) => {
            const { includedPortfolios, excludedPortfolios } = prevState || {};

            if (checked) {
                return {
                    includedPortfolios: includedPortfolios?.concat(item),
                    excludedPortfolios: excludedPortfolios?.filter(pFolio => pFolio?.portfolioId !== item?.portfolioId),
                };
            }

            return {
                includedPortfolios: includedPortfolios?.filter(pFolio => pFolio?.portfolioId !== item?.portfolioId),
                excludedPortfolios: excludedPortfolios?.concat(item),

            };
        });
    }, []);

    const saveData = async (data: AdvisoryInvestmentsResponse) => {
        const payload = { ...data };

        if (advisoryIdSelector) {
            const result:any = await saveAdvisoryDataAction({
                key: 'investments',
                payload,
                setPageErrors,
            });

            if (result?.status === STATUS_OK) {
                await queryClient.invalidateQueries({ queryKey: ['getData_getExistingPortfolios'] });
                unlockNavigationAction();
                stepComplete();
            }
            setContinueButtonLoading(false);
        }
    };

    const onSubmit = useCallback(async () => {
        if (portfolios?.length) {
            setContinueButtonLoading(true);

            await saveData(investments);
        } else {
            unlockNavigationAction();
            stepComplete();
        }
    }, [investments, touched]);

    const { stepComplete, unlockNavigationAction } = usePortfolioProcessNavigation({
        saveOnContinue: onSubmit,
        continueInProgress: continueButtonLoading,
        enableAutoStepComplete: false,
        pageErrors,
    });

    return {
        data: portfolios,
        isLoading: isOverviewLoading,
        isJoint: isJointAccountSelector,
        handleProfileSelection,
        setIsTouched,
    };
};
